import { Show } from '@clerk/react';
import Base from './routes/Base/base';
import IsDiffitAdmin from './components/AdminTools/IsDiffitAdmin';
import ErrorPage from './routes/Base/ErrorPage';
import SignInModal from './components/modals/SignInModal';
import Maintenance from './components/Maintenance';
import { Start } from './routes/Base/start';

import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { overloadStorageForRetries } from './services/useLocalStorage';
import { removePersistedUndoStacks } from './routes/Project/utils/ProjectChangesStack';
import { useHomePath } from './hooks/useHomePath';

overloadStorageForRetries();
removePersistedUndoStacks();

// Redirect component that uses useParams
const PacketRedirect = () => {
  const { packetId } = useParams();
  return <Navigate to={`/p/${packetId}`} replace />;
};

const SharedTemplateRedirect = () => {
  const { templateId } = useParams();
  return <Navigate to={`/?sharedTemplate=${templateId}`} replace />;
};

// Referral share-link short URL (diffit.me/r/<code>). Rewrites the code into
// `?ref=<code>` and redirects home, where Base's referral-capture effect persists it.
const ReferralRedirect = () => {
  const { code } = useParams();
  // Forward the rest of the query string: an invite short link carries ?i=<referral_id>
  // alongside, and dropping it loses the id/code fallback attribution relies on.
  const params = new URLSearchParams(location.search);
  params.set('ref', code);
  return <Navigate to={`/?${params.toString()}`} replace />;
};

const PromoRedirect = () => {
  const search = location.search;
  return <Navigate to={`/redeem${search}`} replace />;
};

// Signed-out branch of /donuts-with-diffit. Stay on the URL and prompt for sign-in in
// place: redirecting here would replace the deep link before SignInModal can capture it
// as the post-sign-in return URL, stranding the teacher on the home page. Dismissing the
// prompt navigates home, so declining to sign in is still an exit — the same shape as
// promo.jsx and DonutsWithDiffit's own close handler.
const DonutsSignedOutPrompt = () => {
  const navigate = useNavigate();
  const homePath = useHomePath();
  return (
    <>
      <Start inline />
      <SignInModal open={true} setOpen={() => navigate(homePath)} />
    </>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Base />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        async lazy() {
          let { HomeRoute } = await import('./routes/Base/HomeRoute');
          return { Component: HomeRoute };
        },
      },
      {
        path: '/for/:slug',
        async lazy() {
          let { LandingPage } = await import('./routes/LandingPage/LandingPage');
          return { Component: LandingPage };
        },
      },
      {
        path: '/classic',
        async lazy() {
          let { Start } = await import('./routes/Base/start');
          return { Component: Start };
        },
      },
      {
        path: '/p/:packetId',
        async lazy() {
          let { Packet } = await import('./routes/Packet/packet');
          return { Component: Packet };
        },
      },
      {
        path: '/c/:collectionId',
        async lazy() {
          let { FolderPage } = await import('./routes/Folder/Folder');
          return { Component: FolderPage };
        },
      },
      {
        path: '/project/:projectId',
        async lazy() {
          let { Project } = await import('./routes/Project/project');
          return { Component: Project };
        },
      },
      {
        path: '/project/:projectId/:packetId',
        async lazy() {
          let { Project } = await import('./routes/Project/project');
          return { Component: Project };
        },
      },
      {
        // Courses proof of concept — availability is decided by the API (403 outside it).
        path: '/courses',
        async lazy() {
          let { Courses } = await import('./routes/Courses/Courses');
          return {
            Component: () => (
              <>
                <Show when='signed-in'>
                  <Courses />
                </Show>
                <Show when='signed-out'>
                  <Start redirect />
                </Show>
              </>
            ),
          };
        },
      },
      {
        path: '/resources',
        async lazy() {
          let { MyResources } = await import('./routes/MyResources/MyResources');
          return {
            Component: () => (
              <>
                <Show when='signed-in'>
                  <MyResources />
                </Show>
                <Show when='signed-out'>
                  <Start redirect />
                </Show>
              </>
            ),
          };
        },
      },
      {
        path: '/donuts-with-diffit',
        async lazy() {
          let { DonutsWithDiffit } = await import('./routes/DonutsWithDiffit/DonutsWithDiffit');
          return {
            Component: () => (
              <>
                <Show when='signed-in'>
                  <Start inline />
                  <DonutsWithDiffit />
                </Show>
                <Show when='signed-out'>
                  <DonutsSignedOutPrompt />
                </Show>
              </>
            ),
          };
        },
      },
      {
        // C&I leader curriculum-alignment request; public unless request-curriculum-sign-in is on.
        path: '/request-curriculum',
        async lazy() {
          let { RequestCurriculum } = await import('./routes/RequestCurriculum/RequestCurriculum');
          return { Component: RequestCurriculum };
        },
      },
      {
        path: '/auth',
        async lazy() {
          let { Auth } = await import('./routes/Auth/auth');
          return { Component: Auth };
        },
      },
      {
        path: '/connect',
        async lazy() {
          let { Connect } = await import('./routes/Connect/connect');
          return { Component: Connect };
        },
      },
      {
        path: '/subscription',
        async lazy() {
          let { ManageStripeSubscription } = await import('./routes/Subscription/subscription');
          return { Component: ManageStripeSubscription };
        },
      },
      {
        path: '/redeem',
        async lazy() {
          let { EnterPromoCode } = await import('./routes/Promo/promo');
          let { Start } = await import('./routes/Base/start');
          return {
            Component: () => (
              <EnterPromoCode>
                <Start inline />
              </EnterPromoCode>
            ),
          };
        },
      },
      {
        path: '/admin',
        async lazy() {
          let { Admin } = await import('./routes/Admin/admin');
          return {
            Component: () => (
              <>
                <IsDiffitAdmin>
                  <Admin />
                </IsDiffitAdmin>
                <Show when='signed-out'>
                  <Start redirect />
                </Show>
              </>
            ),
          };
        },
      },
      {
        path: '/eval',
        async lazy() {
          let { Eval } = await import('./routes/Eval/Eval');
          return {
            Component: () => (
              <>
                <Show when='signed-in'>
                  <Eval />
                </Show>
                <Show when='signed-out'>
                  <Start redirect />
                </Show>
              </>
            ),
          };
        },
      },
      {
        path: '/referral',
        async lazy() {
          let { ReferralStatus } = await import('./routes/Referral/ReferralStatus');
          let { default: ReferralRouteGate } = await import('./routes/Referral/ReferralRouteGate');
          return {
            Component: () => (
              <>
                <Show when='signed-in'>
                  <ReferralRouteGate>
                    <ReferralStatus />
                  </ReferralRouteGate>
                </Show>
                <Show when='signed-out'>
                  <Start redirect />
                </Show>
              </>
            ),
          };
        },
      },
      {
        path: '/org/:orgId',
        async lazy() {
          // Capture SignedIn/SignedOut
          let { OrgDashboard } = await import('./routes/Organization/organization');
          return {
            Component: () => {
              return (
                <>
                  <Show when='signed-in'>
                    <OrgDashboard />
                  </Show>
                  <Show when='signed-out'>
                    <Start redirect />
                  </Show>
                </>
              );
            },
          };
        },
      },
      {
        path: '/district/:districtId',
        async lazy() {
          let { DistrictDashboard } = await import('./routes/District/DistrictDashboard');
          return {
            Component: () => {
              return (
                <>
                  <Show when='signed-in'>
                    <DistrictDashboard />
                  </Show>
                  <Show when='signed-out'>
                    <Start redirect />
                  </Show>
                </>
              );
            },
          };
        },
      },
      {
        path: '/join/:orgId',
        async lazy() {
          let { JoinOrg } = await import('./routes/Join/Org/JoinOrg');
          let { Start } = await import('./routes/Base/start');
          return {
            Component: () => (
              <JoinOrg>
                <Start inline />
              </JoinOrg>
            ),
          };
        },
      },
      {
        path: '/pricing/:quoteId?',
        async lazy() {
          let { PricingPage } = await import('./routes/Pricing/PricingPage');
          return {
            Component: () => <PricingPage />,
          };
        },
      },
      {
        path: '/workshop',
        element: <Navigate to='/' replace />,
      },
      // shared template route
      {
        path: '/t/:templateId',
        element: <SharedTemplateRedirect />,
      },
      // referral share-link route
      {
        path: '/r/:code',
        element: <ReferralRedirect />,
      },

      // DEV-ONLY — pagination eval-suite scoring lab (excluded from prod builds).
      ...(import.meta.env.DEV
        ? [
            {
              path: '/pag-lab',
              async lazy() {
                let { PagLab } = await import('./routes/PagLab/PagLab');
                return { Component: PagLab };
              },
            },
            // Courses lab: scripted course threads for designing the agent's views.
            {
              path: '/courses-lab',
              async lazy() {
                let { CoursesLab } = await import('./routes/Courses/lab/CoursesLab');
                return { Component: CoursesLab };
              },
            },
          ]
        : []),

      //#region DEPRECATED ROUTES

      // packet routes
      {
        path: '/packet/:packetId',
        element: <PacketRedirect />,
      },
      {
        path: '/packet/:packetId/:activityId',
        element: <PacketRedirect />,
      },
      {
        path: '/packet/:packetId/customize',
        element: <PacketRedirect />,
      },

      // activity routes
      {
        path: '/activities',
        element: <Navigate to={'/'} replace />,
      },
      {
        path: '/activities/:packetId',
        element: <Navigate to={'/'} replace />,
      },
      {
        path: '/activity/:activityId',
        element: <Navigate to={'/'} replace />,
      },
      {
        path: '/activity/:activityId/:packetId',
        element: <Navigate to={'/'} replace />,
      },
      {
        path: '/activity/:activityId/:packetId/customize',
        element: <Navigate to={'/'} replace />,
      },

      // templates route
      {
        path: '/templates',
        element: <Navigate to={'/'} replace />,
      },
      {
        path: '/admin/templates',
        element: <Navigate to={'/'} replace />,
      },
      // promo code route
      {
        path: '/promo',
        element: <PromoRedirect />,
      },

      //#endregion
    ],
  },
]);

export default function App() {
  const inMaintenanceMode = false;
  return <>{inMaintenanceMode ? <Maintenance /> : <RouterProvider router={router} />}</>;
}
